import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import { memberRegistrationApi } from '../services/api'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { Card } from '../components/Card'
import logo from '../assets/El CapIT_logo_top_left.png'

const DEFAULT_WAIVER_CONTENT = `ASSUMPTION OF RISK

I understand that rock climbing, bouldering, and related activities involve significant risks of injury or death. I knowingly and voluntarily assume all risks associated with participation at this facility, including but not limited to:

• Falls from any height, including while bouldering or top-roping
• Contact with walls, holds, padding, or other participants
• Equipment failure, whether rented or personal
• Overexertion or physical strain beyond my current fitness level
• Acts or omissions of other participants or staff

RELEASE OF LIABILITY

In consideration of being permitted to use this facility, I hereby release, waive, and discharge the gym, its owners, officers, employees, agents, and volunteers from any and all claims, demands, or causes of action arising out of my participation in climbing activities, including claims arising from negligence.

RULES AND SAFETY

I agree to follow all posted rules, facility policies, and staff instructions at all times, use only equipment I am trained and qualified to use, immediately report any unsafe conditions, injuries, or incidents to staff, and not climb under the influence of alcohol or drugs.

ACKNOWLEDGMENT

I have read this waiver in its entirety, I understand its meaning and legal significance, and I agree to be bound by its terms.`

type Step = 'info' | 'waiver'

export default function CustomerSignup() {
  const [step, setStep] = useState<Step>('info')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [waiverSignature, setWaiverSignature] = useState('')
  const [waiverAgreed, setWaiverAgreed] = useState(false)
  const [waiverContent, setWaiverContent] = useState(DEFAULT_WAIVER_CONTENT)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const { login } = useAuth()
  const { tenant } = useTenant()
  const navigate = useNavigate()

  // Force light mode
  useEffect(() => {
    document.documentElement.classList.remove('dark')
    return () => {
      const stored = localStorage.getItem('user-theme-preference')
      if (stored === 'dark') {
        document.documentElement.classList.add('dark')
      }
    }
  }, [])

  useEffect(() => {
    memberRegistrationApi.getWaiverContent()
      .then(res => {
        if (res.data?.waiverContent) setWaiverContent(res.data.waiverContent)
      })
      .catch(() => {/* use default */})
  }, [])

  function handleInfoSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setStep('waiver')
  }

  async function handleWaiverSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!waiverAgreed) {
      setError('You must agree to the waiver to continue')
      return
    }
    if (!waiverSignature.trim()) {
      setError('Please type your full name as your signature')
      return
    }

    try {
      setLoading(true)
      await memberRegistrationApi.register({
        name,
        email,
        phone,
        password,
        waiverAgreed: true,
        waiverSignature: waiverSignature.trim(),
      })

      await login(email, password)
      navigate('/customer/dashboard')
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Registration failed'
      if (message.toLowerCase().includes('already exists') || message.toLowerCase().includes('conflict')) {
        setError('An account with this email already exists. Please sign in instead.')
      } else {
        setError(message)
      }
      setStep('info')
    } finally {
      setLoading(false)
    }
  }

  const gymLogo = tenant?.branding?.logoUrl
  const gymName = tenant?.branding?.gymName

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {gymLogo ? (
            <img src={gymLogo} alt={gymName} className="h-24 mx-auto mb-6 object-contain" />
          ) : (
            <img src={logo} alt="El CapIT" className="h-48 mx-auto mb-6" />
          )}
        </div>

        {step === 'waiver' ? (
          <Card padding="lg">
            <button
              type="button"
              onClick={() => setStep('info')}
              className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 mb-4"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </button>

            <h2 className="text-2xl font-bold text-neutral-900 mb-1">Liability Waiver</h2>
            <p className="text-sm text-neutral-500 mb-4">Please read and sign before creating your account</p>

            {error && (
              <div className="mb-4 p-4 bg-error-light/20 border border-error-DEFAULT rounded-lg">
                <p className="text-sm text-error-dark">{error}</p>
              </div>
            )}

            <form onSubmit={handleWaiverSubmit} className="space-y-4">
              <div className="h-56 overflow-y-auto border border-neutral-200 rounded-lg p-4 bg-neutral-50 text-xs text-neutral-700 leading-relaxed whitespace-pre-wrap">
                {waiverContent}
              </div>

              <div className="flex items-start gap-3">
                <input
                  id="waiver-agree"
                  type="checkbox"
                  checked={waiverAgreed}
                  onChange={e => setWaiverAgreed(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="waiver-agree" className="text-sm text-neutral-700">
                  I have read and agree to the terms of this waiver
                </label>
              </div>

              <Input
                type="text"
                label="Signature (type your full name)"
                value={waiverSignature}
                onChange={setWaiverSignature}
                placeholder="Your full legal name"
                required
                disabled={loading}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={loading || !waiverAgreed}
                loading={loading}
              >
                {loading ? 'Creating account...' : 'Agree & Create Account'}
              </Button>
            </form>
          </Card>
        ) : (
          <Card padding="lg">
            <h2 className="text-2xl font-bold text-neutral-900 mb-6">Create Account</h2>

            {error && (
              <div className="mb-6 p-4 bg-error-light/20 border border-error-DEFAULT rounded-lg">
                <p className="text-sm text-error-dark">{error}</p>
              </div>
            )}

            <form onSubmit={handleInfoSubmit} className="space-y-5">
              <Input
                type="text"
                label="Full Name"
                value={name}
                onChange={setName}
                placeholder="Jane Smith"
                required
                disabled={loading}
              />

              <Input
                type="email"
                label="Email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                required
                disabled={loading}
              />

              <Input
                type="tel"
                label="Phone"
                value={phone}
                onChange={setPhone}
                placeholder="+1 (555) 000-0000"
                required
                disabled={loading}
              />

              <Input
                type="password"
                label="Password"
                value={password}
                onChange={setPassword}
                placeholder="••••••••"
                required
                disabled={loading}
              />

              <Input
                type="password"
                label="Confirm Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="••••••••"
                required
                disabled={loading}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={loading}
              >
                Continue to Waiver
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-neutral-500">
              Already have an account?{' '}
              <Link to="/customer/login" className="font-medium text-primary-600 hover:underline">
                Sign In
              </Link>
            </p>
          </Card>
        )}

        {gymName && (
          <p className="text-center text-sm text-neutral-500">{gymName} · Powered by El CapIT<br />A BuilderBias product</p>
        )}
      </div>
    </div>
  )
}
